import { Component, OnInit } from '@angular/core';
import { ComercializadorService } from '../services/comercializador.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Tipo } from '../../../interfaces/tipo.interface';
import { catchError, firstValueFrom, forkJoin, of } from 'rxjs';
import { Empresa } from '../interfaces/empresa.interface';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { EmpresaService } from '../services/empresa.service';
import { TranslateService } from '@ngx-translate/core';
import { RegistroService } from 'src/app/registro.service';
import { CargaEmpresasService } from 'src/app/services/carga-empresas.service';
import { etiquetaProducto } from '../utils/etiquetas-producto.util';

@Component({
    selector: 'app-comercializador',
    templateUrl: './comercializador.component.html',
    styleUrls: ['./comercializador.component.css'],
})
export class ComercializadorComponent implements OnInit {
    comercializadorForm!: FormGroup;

    registrosPuntosVenta: Tipo[] = [];
    registrosPuntosVentaMunicipios: Tipo[] = [];
    registrosPuntosVentaCapitales: Tipo[] = [];
    registrosPersonalEmpresa: Tipo[] = [];
    registrosClasificacionPersonal: Tipo[] = [];
    registrosMontoPromedio: Tipo[] = [];
    registrosPorcentajeVentas: Tipo[] = [];
    registrosPuntosVentaProductos: Tipo[] = [];

    loading = true;

    opcionesComercializador = [
        'Fertilizantes',
        'Plaguicidas',
        'Coadyuvantes',
        'Alimentos balanceados para animales',
        'Medicamentos veterinarios',
        'Materiales y otros insumos',
    ];

    etiquetaProducto = etiquetaProducto;

    selectedComercializador: string[] = [];

    /* valores seleccionados en otras preguntas */
    selectedPuntosVenta?: number;
    selectedPersonal?: number;
    empresaData: Empresa;
    perfilCargo: string;
    empresa: Empresa;
    isEditing = false;
    existingComercializadorData: any;
    empresaId: number;
    // en caso de edicion, almacenar el id del comercializador existente
    comercializadorId: number;
    // HU 116 - indica si la caracterización está bloqueada por estar clasificada
    caracterizacionBloqueada = false;

    constructor(
        private fb: FormBuilder,
        private comercializadorService: ComercializadorService,
        private empresaService: EmpresaService,
        private route: ActivatedRoute,
        private router: Router,
        private translate: TranslateService,
        private registroService: RegistroService,
        private cargaEmpresasService: CargaEmpresasService
    ) { }

    /* Inicializacion formulario */
    ngOnInit(): void {
        this.comercializadorForm = this.fb.group({
            comercializador: [null, Validators.required],
            puntosVenta: ['', Validators.required],
            puntosVentaMunicipios: [''],
            puntosVentaCapitales: [''],
            personalEmpresa: ['', Validators.required],
            clasificacionPersonal: [''],
            montoPromedio: ['', Validators.required],
            porcentajeVentas: ['', Validators.required],
            puntosVentaProductos: ['', Validators.required],
        });

        const state = history.state as {
            empresaData?: Empresa;
            perfilCargo?: string;
            agente_vigilado_id?: number;
            isEditing?: boolean;
            pendingEmpresaCreation?: boolean;
        };

        if (!state?.empresaData) {
            Swal.fire({
                title: 'Error',
                text: 'No se ha encontrado la informacion de la empresa',
                icon: 'error',
            });
            this.router.navigate(['/']);
            return;
        }

        this.empresaData = state.empresaData;
        this.perfilCargo = state.perfilCargo;
        this.isEditing = state.isEditing || false;

        this.empresaId = state.agente_vigilado_id;

        this.cargarDatos();
    }

    /** funcion para cargar la informacion de los datos maestros */
    cargarDatos(): void {
        const obsPuntosVenta = this.comercializadorService
            .getPreguntasComercializador('puntos-venta')
            .pipe(catchError(() => of([])));

        const obsPersonalEmpresa = this.comercializadorService
            .getPreguntasComercializador('personal-empresa')
            .pipe(catchError(() => of([])));

        const obsMontoPromedio = this.comercializadorService
            .getPreguntasComercializador('monto-promedio-ventas')
            .pipe(catchError(() => of([])));

        const obsPorcentajeVentas = this.comercializadorService
            .getPreguntasComercializador('porcentaje-ventas')
            .pipe(catchError(() => of([])));

        const obsPuntosVentaProductos = this.comercializadorService
            .getPreguntasComercializador('puntos-venta-productos')
            .pipe(catchError(() => of([])));

        forkJoin([
            obsPuntosVenta,
            obsPersonalEmpresa,
            obsMontoPromedio,
            obsPorcentajeVentas,
            obsPuntosVentaProductos,
        ]).subscribe({
            next: ([
                puntosVenta,
                personalEmpresa,
                montoPromedio,
                porcentajeVentas,
                puntosVentaProductos,
            ]) => {
                this.registrosPuntosVenta = puntosVenta;
                this.registrosPersonalEmpresa = personalEmpresa;
                this.registrosMontoPromedio = montoPromedio;
                this.registrosPorcentajeVentas = porcentajeVentas;
                this.registrosPuntosVentaProductos = puntosVentaProductos;

                // Si es edición, cargar datos existentes
                if (this.isEditing && this.empresaId) {
                    this.cargarDatosExistentes();
                } else {
                    this.loading = false;
                }
            },
            error: () => {
                this.loading = false;
            },
        });
    }

    /** funcion para cargar la informacion de los datos que ya existen */
    async cargarDatosExistentes(): Promise<void> {
        if (history.state?.pendingEmpresaCreation) {
            this.loading = false;
            return;
        }
        try {
            this.existingComercializadorData = await firstValueFrom(
                this.comercializadorService.findByAgenteVigiladoId(
                    this.empresaId
                )
            );

            if (this.existingComercializadorData) {
                this.patchFormWithExistingData();
            }
        } catch (error) {
            this.existingComercializadorData = null;
        } finally {
            this.loading = false;
        }
    }

    /**
     * Carga los datos existentes del comercializador en el formulario reactivo (`comercializadorForm`),
     * permitiendo mostrar y editar la información previamente registrada.
     *
     * Este método se encarga de:
     * - Cargar los grupos de productos seleccionados desde los datos existentes
     * - Actualizar los valores del formulario con la información preexistente
     * - Ejecutar los callbacks correspondientes cuando hay cambios en puntos de venta o personal
     *
     * @remarks
     * El método realiza las siguientes operaciones:
     * 1. Obtiene los datos existentes de this.existingComercializadorData
     * 2. Procesa los grupos de productos separados por comas
     * 3. Aplica los valores al formulario usando patchValue
     * 4. Ejecuta los métodos onPuntosVentaChange y onPersonalChange si existen los IDs correspondientes
     *
     * @throws {Error} Si el formulario comercializadorForm no está inicializado
     */
    private patchFormWithExistingData(): void {
        const data = this.existingComercializadorData;
        this.comercializadorId = data.id;

        if (data.gruposProductos) {
            this.selectedComercializador = data.gruposProductos.split(', ');
            this.comercializadorForm
                .get('comercializador')
                .setValue(this.selectedComercializador);
        }

        this.comercializadorForm.patchValue({
            puntosVenta: data.puntosVentaId?.toString() || '',
            puntosVentaMunicipios:
                data.puntosVentaMunicipiosId?.toString() || '',
            puntosVentaCapitales: data.puntosVentaCapitalesId?.toString() || '',
            personalEmpresa: data.personalEmpresaId?.toString() || '',
            clasificacionPersonal:
                data.clasificacionPersonalEmpresaId?.toString() || '',
            montoPromedio: data.montoPromedioVentasId?.toString() || '',
            porcentajeVentas: data.porcentajeVentasId?.toString() || '',
            puntosVentaProductos: data.puntosVentaProductosId?.toString() || '',
        });

        if (data.puntosVentaId) {
            this.onPuntosVentaChange(data.puntosVentaId);
        }
        if (data.personalEmpresaId) {
            this.onPersonalChange(data.personalEmpresaId);
        }

        // HU 116 - Si la caracterización ya está clasificada, bloquear la edición.
        if (data.estadoClasificacion === 'clasificada') {
            this.caracterizacionBloqueada = true;
            this.comercializadorForm.disable();

            // HU 20618 - Mostrar la ventana informando que la caracterización está
            // bloqueada y, al seleccionar OK, redirigir al usuario al inicio.
            Swal.fire({
                title: 'Caracterización bloqueada',
                text: 'La caracterización de tu empresa ya fue clasificada y no puede ser editada. Si requieres realizar cambios, por favor, comunícate con la mesa de ayuda.',
                icon: 'info',
                confirmButtonText: 'OK',
                confirmButtonColor: '#28a745',
                allowOutsideClick: false,
            }).then(() => {
                this.router.navigate(['/inicio']);
            });
        }
    }

    /**
     * Maneja el cambio en la selección de puntos de venta y ejecuta validaciones para las preguntas 2, 3 y 5.
     *
     * Esta función se activa cuando cambia el valor de puntos de venta y realiza las siguientes acciones:
     * - Obtiene y actualiza los registros de puntos de venta en municipios y capitales
     * - Gestiona dinámicamente las validaciones requeridas para los controles dependientes
     * - Mantiene el estado sincronizado del formulario
     *
     * @remarks
     * Flujo de ejecución:
     * 1. Normaliza el ID a tipo número
     * 2. Almacena el ID seleccionado en selectedPuntosVenta
     * 3. Realiza llamadas al servicio para obtener datos de municipios y capitales
     * 4. Establece o limpia validadores requeridos según la selección
     * 5. Actualiza el estado de validación de los controles afectados
     *
     * Los controles afectados son:
     * - puntosVentaMunicipios
     * - puntosVentaCapitales
     *
     * @throws {Error} Si el servicio getPreguntasComercializador falla
     * @throws {TypeError} Si el ID no puede ser convertido a número
     *
     * @see {@link comercializadorService.getPreguntasComercializador}
     * @see {@link Validators.required}
     *
     * @observable
     * Realiza dos llamadas HTTP asíncronas para obtener:
     * - Registros de puntos de venta en municipios
     * - Registros de puntos de venta en capitales
     *
     * @sideeffects
     * - Modifica selectedPuntosVenta
     * - Actualiza registrosPuntosVentaMunicipios
     * - Actualiza registrosPuntosVentaCapitales
     * - Cambia validadores de puntosVentaMunicipios y puntosVentaCapitales
     * - Dispara change detection al actualizar el formulario
     */
    onPuntosVentaChange(id: number | string): void {
        const idNumber = typeof id === 'string' ? Number(id) : id;
        this.selectedPuntosVenta = idNumber;
        this.comercializadorService
            .getPreguntasComercializador('puntos-venta-municipios', idNumber)
            .subscribe((data) => (this.registrosPuntosVentaMunicipios = data));

        this.comercializadorService
            .getPreguntasComercializador('puntos-venta-capitales', idNumber)
            .subscribe((data) => (this.registrosPuntosVentaCapitales = data));

        const municipiosCtrl = this.comercializadorForm.get(
            'puntosVentaMunicipios'
        );
        const capitalesCtrl = this.comercializadorForm.get(
            'puntosVentaCapitales'
        );

        if (idNumber) {
            municipiosCtrl?.setValidators([Validators.required]);
            capitalesCtrl?.setValidators([Validators.required]);
        } else {
            municipiosCtrl?.clearValidators();
            capitalesCtrl?.clearValidators();
        }

        municipiosCtrl?.updateValueAndValidity();
        capitalesCtrl?.updateValueAndValidity();
    }

    /**
     * Gestiona las validaciones y la carga dinámica de datos relacionados con la pregunta 4 del formulario
     * de comercializador, correspondiente al personal de la empresa.
     *
     * Este método se ejecuta cuando cambia el valor del campo **"personal de la empresa"**.
     * Realiza las siguientes acciones:
     *
     * 1. Convierte el valor recibido (`id`) a número si es una cadena.
     * 2. Actualiza la propiedad `selectedPersonal` con el identificador seleccionado.
     * 3. Solicita al servicio `comercializadorService` la información asociada a la
     *    **clasificación del personal de la empresa**, usando el endpoint
     *    `'clasificacion-personal-empresa'`.
     * 4. Asigna los resultados obtenidos a la propiedad local `registrosClasificacionPersonal`.
     * 5. Aplica validación dinámica al campo `clasificacionPersonal` del formulario:
     *    - Si hay un identificador válido, establece el validador `Validators.required`.
     *    - Si no hay selección, elimina el validador.
     * 6. Actualiza el estado de validación del control correspondiente.
     *
     * @param {number | string} id - Identificador del tipo de personal seleccionado. Puede ser un número o una cadena convertible a número.
     * @returns {void}
     */
    onPersonalChange(id: number | string): void {
        const idNumber = typeof id === 'string' ? Number(id) : id;
        this.selectedPersonal = idNumber;
        this.comercializadorService
            .getPreguntasComercializador(
                'clasificacion-personal-empresa',
                idNumber
            )
            .subscribe((data) => (this.registrosClasificacionPersonal = data));

        const clasificacionCtrl = this.comercializadorForm.get(
            'clasificacionPersonal'
        );

        if (idNumber) {
            clasificacionCtrl?.setValidators([Validators.required]);
        } else {
            clasificacionCtrl?.clearValidators();
        }

        clasificacionCtrl?.updateValueAndValidity();
    }

    /**
     * HU 116 - Intercepta clics en el formulario cuando está bloqueado.
     * Muestra un modal informativo indicando que debe contactar la mesa de ayuda.
     */
    onFormClick(event: Event): void {
        if (!this.caracterizacionBloqueada) return;

        const target = event.target as HTMLElement;
        const tag = target.tagName.toLowerCase();

        // Si hace clic en un select, input o botón de checkbox, mostrar modal
        if (tag === 'select' || tag === 'input' || tag === 'button') {
            event.preventDefault();
            event.stopPropagation();
            Swal.fire({
                title: 'Caracterización bloqueada',
                text: 'La caracterización de tu empresa ya fue clasificada y no puede ser editada. Si requieres realizar cambios, por favor, comunícate con la mesa de ayuda.',
                icon: 'info',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#28a745',
            });
        }
    }

    /** funcion para añadir el comercializador */
    addComercializador(opcion: string, event?: Event): void {
        event?.preventDefault();
        if (!this.selectedComercializador.includes(opcion)) {
            this.selectedComercializador.push(opcion);
            const control = this.comercializadorForm.get('comercializador');
            control?.setValue(this.selectedComercializador);
            control?.markAsTouched();
            control?.updateValueAndValidity();
        }
    }

    /** funcion para eliminar el comercializador */
    removeComercializador(opcion: string, event?: Event): void {
        event?.preventDefault();
        this.selectedComercializador = this.selectedComercializador.filter(
            (o) => o !== opcion
        );
        const control = this.comercializadorForm.get('comercializador');
        control?.setValue(this.selectedComercializador);
        control?.markAsTouched();
        control?.updateValueAndValidity();
    }

    /**
     * Procesa el envío del formulario de comercializador, validando los datos y ejecutando
     * las operaciones correspondientes según el contexto (creación o edición).
     *
     * Este método maneja tres escenarios principales:
     * 1. Creación completa: Empresa + Caracterización (cuando hay pendingEmpresaCreation)
     * 2. Edición: Solo actualización de caracterización (cuando isEditing es true)
     * 3. Error: Intento de crear solo caracterización sin empresa previa
     */
    async submitForm(): Promise<void> {
        // HU 116 - No permitir guardar si la caracterización está clasificada.
        if (this.caracterizacionBloqueada) {
            Swal.fire(
                'Información',
                'Si requieres editar la caracterización de tu empresa, por favor, comunícate con la mesa de ayuda.',
                'info'
            );
            return;
        }

        const formValue = this.comercializadorForm.value;
        if (this.comercializadorForm.invalid) {
            this.comercializadorForm.markAllAsTouched();
            Swal.fire(
                'Error',
                'Por favor complete todos los campos obligatorios',
                'error'
            );
            return;
        }

        this.loading = true;

        try {
            /* CASO 1: Crear empresa Y caracterización (creación completa)*/
            if (history.state?.pendingEmpresaCreation && this.empresaData) {
                await this.crearCaracterizacionComercializador(
                    this.empresaData,
                    formValue
                );
            } else if (this.isEditing && this.empresaId) {
                if (this.comercializadorId) {
                    /* CASO 2a: Edición (solo caracterización) pues ya había sido creada previamente*/
                    await this.actualizarEmpresaYCaracterizacion(
                        this.empresaData,
                        formValue,
                        this.empresaId,
                        this.comercializadorId
                    );
                } else {
                    /* CASO 2b: Edición pero no existía comercializador - crear uno nuevo 
                        Esto pasa cuando se cambia el eslabon de una empresa existente a comercializador
                    */
                    await this.crearCaracterizacionComercializador(
                        this.empresaData,
                        formValue,
                        this.empresaId,
                    );
                }
            } else {
                /* CASO 3: si se crea comercializadornsin la empresa - daria error */
                throw new Error(
                    'No se puede crear solo la caracterización. La empresa debe crearse primero.'
                );
            }

            const translated = await firstValueFrom(
                this.translate.get(
                    this.isEditing
                        ? 'empresa.alertaEdicionExitosa'
                        : 'empresa.alertaExitosa'
                )
            );
            await Swal.fire('', translated, 'success');

            // Refrescamos la lista de empresas con datos frescos del backend.
            // No parcheamos eslabones manualmente: el backend ya devuelve el
            // estado correcto de cada empresa.
            try {
                const empresas = await firstValueFrom(
                    this.cargaEmpresasService.obtenerEmpresas()
                );
                this.registroService.listaEmpresasAsociadas = empresas;
            } catch (e) {
                console.error('Error obteniendo empresas', e);
            }

            // Solo marcar empresasAsociadas = true si se edita una empresa
            // existente (ya aprobada). Al crear una empresa nueva, no se activa
            // el menú completo porque aún no ha sido aprobada.
            const esCreacionEmpresaNueva = !!history.state?.pendingEmpresaCreation;
            if (!esCreacionEmpresaNueva) {
                this.registroService.empresasAsociadas = true;
            }
            const editaEmpresaSeleccionada =
                !esCreacionEmpresaNueva &&
                this.empresaId != null &&
                String(this.empresaId) ===
                    String(this.registroService.empresaSelected);

            if (editaEmpresaSeleccionada && this.empresaData?.eslabon) {
                this.registroService.eslabonEmpresaSelected =
                    this.empresaData.eslabon;
            }

            await this.router.navigate(['/inicio']);
        } catch (err: any) {
            let errorMessage = 'Ocurrió un error al guardar la información.';
            if (err.status === 404) {
                errorMessage =
                    'Endpoint no encontrado. Verifica que el backend esté ejecutándose y la ruta sea correcta.';
            } else if (err.status === 400) {
                errorMessage =
                    'Error en la solicitud: ' +
                    (err.error?.message || 'Verifique los datos.');
            } else if (err.message) {
                errorMessage = err.message;
            }
            Swal.fire('Error', errorMessage, 'error');
        } finally {
            this.loading = false;
        }
    }

    /**
     * Crea simultáneamente una nueva empresa y su caracterización como comercializador
     * o le asocia una caracterización a una empresa existente;
     * todo en una sola operación transaccional hacia el backend.
     *
     * Este método se utiliza cuando se está realizando un registro completo por primera vez,
     * combinando los datos básicos de la empresa con la información específica del comercializador.
     *
     * @remarks
     * El payload construido contiene:
     * - agenteVigilado: Datos básicos de la empresa (eslabón, tipo, NIT, razón social, etc.)
     * - agenteVigiladoId: ID de la empresa existente (si aplica)
     * - gruposProductos: Lista de grupos de productos comercializados
     * - IDs de referencia para las preguntas específicas del formulario
     *
     * @param {any} empresaData - Datos básicos de la empresa obtenidos del formulario principal
     * @param {any} caracterizacionData - Datos específicos del comercializador del formulario actual
     * @param {any} empresaId - Enviar el id de la empresa en caso de que ya exista.
     * @returns {Promise<void>} Promesa que se resuelve cuando la operación se completa exitosamente
     *
     * @throws {HttpErrorResponse} Si el backend devuelve un error HTTP
     * @throws {Error} Si hay problemas en la construcción del payload o en la comunicación
     */
    private async crearCaracterizacionComercializador(
        empresaData: any,
        caracterizacionData: any,
        empresaId?: number
    ): Promise<void> {
        if (!empresaId && !empresaData) {
            throw new Error('Debe proporcionar empresaData o empresaId');
        }

        if (!caracterizacionData?.comercializador) {
            throw new Error('Los grupos de productos son requeridos');
        }

        const payload = {
            // Empresa existente
            agenteVigiladoId: empresaId,
            // Datos de la empresa ya sea para creación o actualización.
            agenteVigilado: empresaData,
            // Caracterización del comercializador
            gruposProductos: caracterizacionData.comercializador.join(', '),
            puntosVentaId: Number(caracterizacionData.puntosVenta) || null,
            puntosVentaMunicipiosId:
                Number(caracterizacionData.puntosVentaMunicipios) || null,
            puntosVentaCapitalesId:
                Number(caracterizacionData.puntosVentaCapitales) || null,
            personalEmpresaId:
                Number(caracterizacionData.personalEmpresa) || null,
            clasificacionPersonalEmpresaId:
                Number(caracterizacionData.clasificacionPersonal) || null,
            montoPromedioVentasId:
                Number(caracterizacionData.montoPromedio) || null,
            porcentajeVentasId:
                Number(caracterizacionData.porcentajeVentas) || null,
            puntosVentaProductosId:
                Number(caracterizacionData.puntosVentaProductos) || null,
        };
        try {
            const resultado = await firstValueFrom(
                this.empresaService.crearComercializador(payload)
            );
            if (resultado && resultado.id) {
                this.empresaId = resultado.id;
            }
        } catch (error: any) {
            console.error('Error completo:', error);
            if (error.error) {
                console.error('Error del backend:', error.error);
            }
            throw error;
        }
    }

    /**
     * Actualiza simultáneamente una empresa y su caracterización como comercializador
     * en una sola operación transaccional hacia el backend.
     *
     * Este método se utiliza cuando se está realizando un registro completo por primera vez,
     * combinando los datos básicos de la empresa con la información específica del comercializador.
     *
     * @remarks
     * El payload construido contiene:
     * - agenteVigilado: Datos básicos de la empresa (eslabón, tipo, NIT, razón social, etc.)
     * - gruposProductos: Lista de grupos de productos comercializados
     * - IDs de referencia para las preguntas específicas del formulario
     *
     * @param {any} empresaData - Datos básicos de la empresa obtenidos del formulario principal
     * @param {any} caracterizacionData - Datos específicos del comercializador del formulario actual
     * @returns {Promise<void>} Promesa que se resuelve cuando la operación se completa exitosamente
     *
     * @throws {HttpErrorResponse} Si el backend devuelve un error HTTP
     * @throws {Error} Si hay problemas en la construcción del payload o en la comunicación
     */
    private async actualizarEmpresaYCaracterizacion(
        empresaData: any,
        caracterizacionData: any,
        empresaId: number,
        comercializadorId: number
    ): Promise<void> {
        if (!empresaData) {
            throw new Error('Debe proporcionar empresaData');
        }

        if (!caracterizacionData?.comercializador) {
            throw new Error('Los grupos de productos son requeridos');
        }

        const payload = {
            id: comercializadorId,
            // Empresa existente
            agenteVigilado: empresaData,
            agenteVigiladoId: empresaId,
            // Caracterización del comercializador
            gruposProductos: caracterizacionData.comercializador.join(', '),
            puntosVentaId: Number(caracterizacionData.puntosVenta) || null,
            puntosVentaMunicipiosId:
                Number(caracterizacionData.puntosVentaMunicipios) || null,
            puntosVentaCapitalesId:
                Number(caracterizacionData.puntosVentaCapitales) || null,
            personalEmpresaId:
                Number(caracterizacionData.personalEmpresa) || null,
            clasificacionPersonalEmpresaId:
                Number(caracterizacionData.clasificacionPersonal) || null,
            montoPromedioVentasId:
                Number(caracterizacionData.montoPromedio) || null,
            porcentajeVentasId:
                Number(caracterizacionData.porcentajeVentas) || null,
            puntosVentaProductosId:
                Number(caracterizacionData.puntosVentaProductos) || null,
        };
        try {
            const resultado = await firstValueFrom(
                this.empresaService.actualizarComercializador(payload)
            );
            if (resultado && resultado.id) {
                this.empresaId = resultado.id;
            }
        } catch (error: any) {
            console.error('Error completo:', error);
            if (error.error) {
                console.error('Error del backend:', error.error);
            }
            throw error;
        }
    }
}
